/**
 * MerchantHiveModule — renders the Merchant Hive world (kind 'merchant'):
 * a flat artificial ground plane carrying the full generated PBR set
 * (albedo + normal + bump + roughness + metalness — tools/asset-pipeline
 * out/ground, served from MinIO), one deterministic mega city of shops
 * (lib/merchant_hive — instanced per size tier: bodies, awnings, lit signs),
 * an arrival beacon at the plaza, and a near-ring of box colliders so shops
 * are solid without drowning Rapier in ~12k static bodies.
 *
 * Lifecycle mirrors the settlement module: build on entering the hive,
 * dispose EVERYTHING on leaving (the sector-lifetime law — geometries,
 * materials, textures, colliders).
 */
import * as THREE from 'three/webgpu';
import { markMrtOverlay } from 'enginex/render/post/mrtOverlay.js';
import type { App, IModule } from 'enginex';
import { disposeLater } from '../lib/gpu_dispose.js';
import { seatHemisphereUp } from '../lib/local_origin.js';
import { atlas } from '../state.js';
import { bus } from '../bus.js';
import { Phase, makeSystem } from '../engine-compat.js';
import { HIVE_PLAZA_R } from '../lib/merchant_hive.js';
import { premiumLots, lotByKey, LOT_RENT, HIVE_PREMIUM_R, type PremiumLot, type LotTier } from '../lib/merchant_lots.js';
import { enterables, type EnterableBuilding } from '../lib/building_interiors.js';
import { makeWindow, type UiWindow } from '../lib/ui_window.js';
import type { AssetModule } from './AssetModule.js';
import type { PhysicsModule } from './PhysicsModule.js';

const GROUND_VISUAL_M = 12_288;   // visual apron well past the 4096 m world
const GROUND_TILE_M = 8;          // one texture tile every 8 m
const COLLIDER_NEAR_M = 110;      // shops become solid inside this ring
const COLLIDER_FAR_M = 160;       // and release beyond this (hysteresis)
const COLLIDER_SWEEP_TICKS = 90;  // ~1.5 s at 60 Hz

/** Muted storefront palettes per tier (warm brick voices + cool steel). */
const TIER_PALETTES: THREE.Color[][] = [
  ['#7a5a48', '#6d5a50', '#815d43', '#5f5a55'].map((c) => new THREE.Color(c)), // stalls
  ['#8a6a50', '#707a80', '#96704f', '#657078'].map((c) => new THREE.Color(c)), // shops
  ['#9a7a58', '#788897', '#8d7462', '#5d6d80'].map((c) => new THREE.Color(c)), // emporiums
  ['#a08560', '#8898a8', '#957f68', '#70808f'].map((c) => new THREE.Color(c)), // mega-halls
];
const SIGN_COLORS = ['#ffb45e', '#7fd4ff', '#ff8d7a', '#a9ff9e', '#e8a9ff', '#ffe27a'].map((c) => new THREE.Color(c));

type LotRowLite = { lotId: bigint; lotKey: string; tier: string; renter: { isEqual(o: unknown): boolean; toHexString(): string }; storeName: string; expiresAt: { microsSinceUnixEpoch: bigint } };

export class MerchantHiveModule implements IModule {
  readonly name = 'MerchantHive';
  private scene!: THREE.Scene;
  private root: THREE.Group | null = null;
  private glow: THREE.HemisphereLight | null = null;
  private builtSeed = '';
  private colliderIds = new Set<string>();
  private sweepAcc = 0;
  // ── The premium district (2026-08-29): the great square + rentable stores ──
  private premium: Array<{ lot: PremiumLot; group: THREE.Group; signTex: THREE.CanvasTexture; signCv: HTMLCanvasElement; lastSign: string; hidden: boolean }> = [];
  private squareColliders: string[] = [];
  private subbedMarket = false;
  private uiTimer: number | null = null;
  private prompt: HTMLDivElement | null = null;
  private panel: UiWindow | null = null;
  private panelLotKey = '';
  private panelSig = '';
  private plates: THREE.Mesh[] = [];
  private platesFor = '';
  private platesSig = '';
  private onZ: ((e: KeyboardEvent) => void) | null = null;
  private nearLotKey = '';

  constructor(private readonly assets: AssetModule, private readonly physics: PhysicsModule) {}

  init(app: App): void {
    this.scene = app.world.scene;
    bus.on('atlas:world-entered', () => this.refresh());
    bus.on('atlas:account-ready', () => this.refresh());
    app.world.addSystem(makeSystem('MerchantHiveColliders', Phase.FixedSim, 86, () => this.tickColliders()));
  }

  private hiveWorld(): { worldId: bigint; terrainSeed: string } | null {
    const conn = atlas.conn;
    if (!conn) return null;
    for (const w of conn.db.world.iter()) if (w.kind === 'merchant') return w;
    return null;
  }

  private refresh(): void {
    const hive = this.hiveWorld();
    const inHive = hive !== null && atlas.currentWorldId === hive.worldId;
    if (!inHive) { this.release(); return; }
    if (this.root && this.builtSeed === hive.terrainSeed) return;
    this.release();
    this.build(hive.terrainSeed);
  }

  // ── Build ──────────────────────────────────────────────────────────────────
  private build(seed: string): void {
    const root = new THREE.Group();
    root.name = 'merchant-hive';
    this.root = root;
    this.builtSeed = seed;
    this.scene.add(root);

    // Ground: one flat plane, the full generated PBR set (brick + metal).
    const groundGeo = new THREE.PlaneGeometry(GROUND_VISUAL_M, GROUND_VISUAL_M, 1, 1);
    groundGeo.rotateX(-Math.PI / 2);
    const ground = new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: 0.85, metalness: 1.0 });
    const repeat = GROUND_VISUAL_M / GROUND_TILE_M;
    const bind = (tex: THREE.Texture): THREE.Texture => {
      tex.repeat.set(repeat, repeat);
      tex.anisotropy = 8;
      return tex;
    };
    void Promise.all([
      this.assets.loadTexture('textures/ground/hive_brick.png'),
      this.assets.loadTexture('textures/ground/hive_brick_n.png', false),
      this.assets.loadTexture('textures/ground/hive_brick_r.png', false),
      this.assets.loadTexture('textures/ground/hive_brick_m.png', false),
      this.assets.loadTexture('textures/ground/hive_brick_h.png', false),
    ]).then(([albedo, normal, rough, metal, bump]) => {
      if (this.root !== root) return; // left the hive while loading
      ground.map = bind(albedo);
      ground.normalMap = bind(normal);
      ground.normalScale.set(0.9, 0.9);
      ground.roughnessMap = bind(rough);
      ground.metalnessMap = bind(metal);
      ground.bumpMap = bind(bump);
      ground.bumpScale = 0.18;
      ground.needsUpdate = true;
    }).catch(() => { /* asset store down — flat gray ground still walks */ });
    const groundMesh = new THREE.Mesh(groundGeo, ground);
    groundMesh.receiveShadow = true;
    root.add(groundMesh);

    // (2026-08-29 rework: the old instanced shop city is GONE — the whole
    // planet is the premium leaseable district now.)

    // City glow: a mega commercial hive is never dark — a soft hemisphere
    // keeps streets readable through the world's own night, and a warm plaza
    // point marks the arrival. (The hive rides a normal day/night sky clock;
    // these only lift the floor, they don't fight the sun.)
    const glow = new THREE.HemisphereLight(0xbfd2e8, 0x54402e, 0.42);
    root.add(glow);
    this.glow = glow; seatHemisphereUp(glow); // three reads the sky side from the light's WORLD position: under a shifted scene the default seat lit the streets from the side
    const plazaLight = new THREE.PointLight(0xffc88a, 90, 160, 1.8);
    plazaLight.position.set(0, 9, 0);
    root.add(plazaLight);

    // A SOLID physics ground for the whole hive (the commons has its terrain
    // trimesh under every interior; here the outdoor clamp is analytic and
    // interiors DISABLE it — without this box, tunneling the 8 cm interior
    // slab dropped players into the void).
    // Top at y −0.10, NOT 0: outdoors the analytic clamp holds the capsule at
    // exactly y 0 — a box top there puts the clamp and depenetration in a
    // 60 Hz epsilon tug-of-war (the TWO-GROUND-AUTHORITIES login-bounce law;
    // field report: "jittery on the hive terrain, smooth indoors"). Sunk a
    // hand's breadth, it never touches a standing capsule and still catches
    // anyone who tunnels an interior slab.
    this.physics.addStaticCollider('hive:ground', { shape: 'box', halfExtents: [2400, 1, 2400] },
      { x: 0, y: -1.1, z: 0, yaw: 0, scale: 1 });
    this.squareColliders.push('hive:ground');

    // The GREAT SQUARE at spawn + the premium rentable district around it.
    this.buildSquare(root);
    this.buildPremium(root);
    this.ensureMarketSubs();
    this.startUiLoop();
  }

  // ── The great square: pavement, fountain, colonnade, banners, arch gates ──
  private buildSquare(root: THREE.Group): void {
    const stone = new THREE.MeshStandardMaterial({ color: 0xb8b0a4, roughness: 0.38, metalness: 0.12 });
    const darkStone = new THREE.MeshStandardMaterial({ color: 0x6d675e, roughness: 0.5, metalness: 0.1 });
    const accent = new THREE.MeshStandardMaterial({ color: 0x8a5c3a, roughness: 0.45, metalness: 0.2 });
    const brass = new THREE.MeshStandardMaterial({ color: 0xc8a24e, roughness: 0.3, metalness: 0.85 });
    const water = new THREE.MeshBasicMaterial({ color: 0x7fd8ff, toneMapped: false, transparent: true, opacity: 0.8 });
    const foliage = new THREE.MeshStandardMaterial({ color: 0x4a7a3a, roughness: 0.9 });

    // Pavement: polished disc + two inlay rings.
    const disc = new THREE.Mesh(new THREE.CircleGeometry(HIVE_PLAZA_R, 72).rotateX(-Math.PI / 2), stone);
    disc.position.y = 0.02;
    disc.receiveShadow = true;
    root.add(disc);
    for (const [r0, r1] of [[17, 21], [76, 81]] as Array<[number, number]>) {
      const ring = new THREE.Mesh(new THREE.RingGeometry(r0, r1, 72).rotateX(-Math.PI / 2), accent);
      ring.position.y = 0.03;
      root.add(ring);
    }

    // The fountain: three stone tiers, glowing water, a brass spire.
    const tiers: Array<[number, number, number]> = [[10, 0.9, 0], [6.5, 1.4, 0.9], [3.2, 1.8, 2.3]];
    for (const [r, h, y0] of tiers) {
      const tier = new THREE.Mesh(new THREE.CylinderGeometry(r, r + 0.4, h, 40), darkStone);
      tier.position.y = y0 + h / 2;
      root.add(tier);
      const pool = new THREE.Mesh(new THREE.CircleGeometry(r - 0.35, 40).rotateX(-Math.PI / 2), water);
      pool.position.y = y0 + h + 0.02;
      root.add(pool);
    }
    const spire = new THREE.Mesh(new THREE.ConeGeometry(0.5, 4.4, 12), brass);
    spire.position.y = 4.3 + 2.2;
    root.add(spire);
    const fountainLight = new THREE.PointLight(0xaee8ff, 55, 90, 1.9);
    fountainLight.position.set(0, 7, 0);
    root.add(fountainLight);

    // Colonnade at r 56: 24 columns under a continuous architrave ring.
    const colGeo = new THREE.CylinderGeometry(0.55, 0.62, 7, 12);
    const capGeo = new THREE.BoxGeometry(1.6, 0.5, 1.6);
    for (let i = 0; i < 24; i++) {
      const t = (i / 24) * Math.PI * 2;
      if (Math.abs(Math.sin(2 * t)) < 0.12 && Math.abs(Math.cos(2 * t)) > 0.98) continue; // clear the boulevard mouths
      const x = Math.sin(t) * 56, z = Math.cos(t) * 56;
      const col = new THREE.Mesh(colGeo, stone);
      col.position.set(x, 3.5, z);
      root.add(col);
      const cap = new THREE.Mesh(capGeo, darkStone);
      cap.position.set(x, 7.25, z);
      root.add(cap);
      this.physics.addStaticCollider(`hivesq:col:${i}`, { shape: 'box', halfExtents: [0.62, 3.5, 0.62] }, { x, y: 3.5, z, yaw: 0, scale: 1 });
      this.squareColliders.push(`hivesq:col:${i}`);
    }
    const archi = new THREE.Mesh(new THREE.TorusGeometry(56, 0.42, 8, 96).rotateX(Math.PI / 2), darkStone);
    archi.position.y = 7.6;
    root.add(archi);

    // Banner ring at r 40 + benches and planters between.
    const poleGeo = new THREE.CylinderGeometry(0.12, 0.14, 9, 8);
    const bannerGeo = new THREE.PlaneGeometry(1.7, 4.4);
    const bannerColors = [0xb2452f, 0x2f6ab2, 0xb28a2f, 0x3a8a58];
    for (let i = 0; i < 8; i++) {
      const t = (i / 8) * Math.PI * 2 + Math.PI / 8;
      const x = Math.sin(t) * 40, z = Math.cos(t) * 40;
      const pole = new THREE.Mesh(poleGeo, brass);
      pole.position.set(x, 4.5, z);
      root.add(pole);
      const banner = new THREE.Mesh(bannerGeo, new THREE.MeshStandardMaterial({ color: bannerColors[i % 4], roughness: 0.85, side: THREE.DoubleSide }));
      banner.position.set(x, 6.2, z);
      banner.rotation.y = t + Math.PI / 2;
      root.add(banner);
      // A bench and a planter beside each pole.
      const bench = new THREE.Mesh(new THREE.BoxGeometry(2.4, 0.42, 0.65), darkStone);
      const bt = t + Math.PI / 16;
      bench.position.set(Math.sin(bt) * 33, 0.24, Math.cos(bt) * 33);
      bench.rotation.y = bt;
      root.add(bench);
      const planter = new THREE.Mesh(new THREE.CylinderGeometry(0.85, 0.95, 0.7, 12), accent);
      const pt = t - Math.PI / 16;
      planter.position.set(Math.sin(pt) * 33, 0.35, Math.cos(pt) * 33);
      root.add(planter);
      const bush = new THREE.Mesh(new THREE.IcosahedronGeometry(0.85, 1), foliage);
      bush.position.set(Math.sin(pt) * 33, 1.1, Math.cos(pt) * 33);
      root.add(bush);
    }

    // Four arch gates where the boulevards leave the square.
    const pillarGeo = new THREE.BoxGeometry(1.3, 9, 1.3);
    const lintelGeo = new THREE.BoxGeometry(14, 1.7, 1.5);
    for (let i = 0; i < 4; i++) {
      const t = (i * Math.PI) / 2;
      const gx = Math.sin(t) * (HIVE_PLAZA_R - 2), gz = Math.cos(t) * (HIVE_PLAZA_R - 2);
      const lateral = new THREE.Vector3(Math.cos(t), 0, -Math.sin(t));
      for (const sgn of [-1, 1]) {
        const px = gx + lateral.x * 6 * sgn, pz = gz + lateral.z * 6 * sgn;
        const pillar = new THREE.Mesh(pillarGeo, stone);
        pillar.position.set(px, 4.5, pz);
        pillar.rotation.y = t;
        root.add(pillar);
        this.physics.addStaticCollider(`hivesq:arch:${i}:${sgn}`, { shape: 'box', halfExtents: [0.65, 4.5, 0.65] }, { x: px, y: 4.5, z: pz, yaw: t, scale: 1 });
        this.squareColliders.push(`hivesq:arch:${i}:${sgn}`);
      }
      const lintel = new THREE.Mesh(lintelGeo, darkStone);
      lintel.position.set(gx, 9.4, gz);
      lintel.rotation.y = t;
      root.add(lintel);
      const cv = document.createElement('canvas');
      cv.width = 512; cv.height = 64;
      const g = cv.getContext('2d')!;
      g.fillStyle = '#1c1712'; g.fillRect(0, 0, 512, 64);
      g.font = '600 34px Georgia, serif';
      g.textAlign = 'center';
      g.fillStyle = '#e8c87a';
      g.fillText('✦ MERCHANT HIVE ✦', 256, 43);
      const tex = new THREE.CanvasTexture(cv);
      tex.colorSpace = THREE.SRGBColorSpace;
      const sign = new THREE.Mesh(new THREE.PlaneGeometry(12, 1.4), new THREE.MeshBasicMaterial({ map: tex, toneMapped: false, side: THREE.DoubleSide }));
      sign.position.set(gx - Math.sin(t) * 0.8, 9.4, gz - Math.cos(t) * 0.8);
      sign.rotation.y = t + Math.PI;
      root.add(sign);
    }
    // Fountain base is solid.
    this.physics.addStaticCollider('hivesq:fountain', { shape: 'box', halfExtents: [10, 2.5, 10] }, { x: 0, y: 2.5, z: 0, yaw: 0, scale: 1 });
    this.squareColliders.push('hivesq:fountain');
  }

  // ── The premium district: 36 elegant rentable stores around the square ──
  private buildPremium(root: THREE.Group): void {
    const TIER_BODY: Record<LotTier, number> = { boutique: 0xd8cfc0, gallery: 0x9aa4ae, grand: 0x4a4f58 };
    const TIER_TRIM: Record<LotTier, number> = { boutique: 0x8a5c3a, gallery: 0x2f4a62, grand: 0xc8a24e };
    const glass = new THREE.MeshStandardMaterial({ color: 0x9fc8e8, roughness: 0.08, metalness: 0.4, transparent: true, opacity: 0.32 });
    const glow = markMrtOverlay(new THREE.MeshBasicMaterial({ color: 0xffdca8, toneMapped: false, transparent: true, opacity: 0.55, side: THREE.DoubleSide }));
    for (const lot of premiumLots()) {
      const g = new THREE.Group();
      g.position.set(lot.x, 0, lot.z);
      g.rotation.y = lot.yaw;
      const body = new THREE.MeshStandardMaterial({ color: TIER_BODY[lot.tier], roughness: 0.6, metalness: 0.12 });
      const trim = new THREE.MeshStandardMaterial({ color: TIER_TRIM[lot.tier], roughness: 0.4, metalness: lot.tier === 'grand' ? 0.75 : 0.3 });
      // Plinth, body, cornice.
      const plinth = new THREE.Mesh(new THREE.BoxGeometry(lot.w + 1.2, 0.4, lot.d + 1.2), trim);
      plinth.position.y = 0.2;
      g.add(plinth);
      const shell = new THREE.Mesh(new THREE.BoxGeometry(lot.w, lot.h, lot.d), body);
      shell.position.y = lot.h / 2;
      shell.castShadow = true;
      g.add(shell);
      const cornice = new THREE.Mesh(new THREE.BoxGeometry(lot.w + 0.9, 0.45, lot.d + 0.9), trim);
      cornice.position.y = lot.h + 0.22;
      g.add(cornice);
      // Pilasters framing the front (local -Z).
      for (const px of [-lot.w / 2 + 0.4, -lot.w / 4, lot.w / 4, lot.w / 2 - 0.4]) {
        const pil = new THREE.Mesh(new THREE.BoxGeometry(0.38, lot.h, 0.38), trim);
        pil.position.set(px, lot.h / 2, -lot.d / 2 - 0.12);
        g.add(pil);
      }
      // Glazing: two panes flanking the door + the warm interior glow behind.
      const paneW = Math.max(1.2, (lot.w * 0.74 - 2.4) / 2);
      const paneH = lot.h * 0.52;
      for (const sgn of [-1, 1]) {
        const pane = new THREE.Mesh(new THREE.PlaneGeometry(paneW, paneH), glass);
        pane.position.set(sgn * (1.2 + paneW / 2), 0.6 + paneH / 2, -lot.d / 2 - 0.03);
        pane.rotation.y = Math.PI;
        g.add(pane);
      }
      const innerGlow = new THREE.Mesh(new THREE.PlaneGeometry(lot.w * 0.6, 2.4), glow);
      innerGlow.position.set(0, 1.8, -lot.d / 2 + 0.5);
      g.add(innerGlow);
      // Door frame (the gap the interior door will occupy).
      for (const sgn of [-1, 1]) {
        const jamb = new THREE.Mesh(new THREE.BoxGeometry(0.22, 3.0, 0.3), trim);
        jamb.position.set(sgn * 1.15, 1.5, -lot.d / 2 - 0.1);
        g.add(jamb);
      }
      const lintel2 = new THREE.Mesh(new THREE.BoxGeometry(2.6, 0.25, 0.3), trim);
      lintel2.position.set(0, 3.05, -lot.d / 2 - 0.1);
      g.add(lintel2);
      // Awning + the fascia sign.
      const awning = new THREE.Mesh(new THREE.BoxGeometry(lot.w * 0.8, 0.12, 1.6), trim);
      awning.position.set(0, 3.35, -lot.d / 2 - 0.9);
      g.add(awning);
      const signCv = document.createElement('canvas');
      signCv.width = 512; signCv.height = 96;
      const signTex = new THREE.CanvasTexture(signCv);
      signTex.colorSpace = THREE.SRGBColorSpace;
      const fascia = new THREE.Mesh(new THREE.PlaneGeometry(lot.w * 0.72, Math.min(1.4, lot.h * 0.16)),
        new THREE.MeshBasicMaterial({ map: signTex, toneMapped: false }));
      fascia.position.set(0, Math.min(lot.h * 0.78, lot.h - 0.7), -lot.d / 2 - 0.16);
      fascia.rotation.y = Math.PI;
      g.add(fascia);
      root.add(g);
      const entry = { lot, group: g, signTex, signCv, lastSign: '', hidden: false };
      this.premium.push(entry);
      this.drawSign(entry);
      // Enterable: the full interior rides the hive:store spec.
      const physics = this.physics;
      const cid = `hivelot:${lot.key}`;
      const ent: EnterableBuilding = {
        id: cid,
        name: 'vacant store',
        key: 'hive:store',
        x: lot.x, z: lot.z, yaw: lot.yaw, groundY: 0,
        w: lot.w, h: lot.h, d: lot.d,
        hide: () => { entry.hidden = true; g.visible = false; physics.removeStaticCollider(cid); this.colliderIds.delete(cid); },
        restore: () => { entry.hidden = false; g.visible = true; },
      };
      enterables.set(cid, ent);
    }
  }

  /** Fascia text: the tenant's store name, or the lease offer. */
  private drawSign(e: { lot: PremiumLot; signTex: THREE.CanvasTexture; signCv: HTMLCanvasElement; lastSign: string }): void {
    const row = this.lotRow(e.lot.key);
    const active = row && Number(row.expiresAt.microsSinceUnixEpoch / 1000n) > Date.now();
    const text = active ? row!.storeName : `FOR LEASE · ${e.lot.tier.toUpperCase()}`;
    if (text === e.lastSign) return;
    e.lastSign = text;
    const g = e.signCv.getContext('2d')!;
    g.fillStyle = active ? '#221a12' : '#14181e';
    g.fillRect(0, 0, 512, 96);
    g.strokeStyle = active ? '#c8a24e' : '#4a6a8a';
    g.lineWidth = 5;
    g.strokeRect(6, 6, 500, 84);
    g.font = active ? '600 44px Georgia, serif' : '600 34px Georgia, serif';
    g.textAlign = 'center';
    g.fillStyle = active ? '#e8c87a' : '#8fb8d8';
    g.fillText(text.slice(0, 22), 256, active ? 62 : 58);
    e.signTex.needsUpdate = true;
  }

  /** Solidify shops around the player; release the ring behind them. */
  private tickColliders(): void {
    if (!this.root) return;
    if (this.glow) seatHemisphereUp(this.glow); // THE HEMISPHERE'S UP: the rebase (FixedSim −5, before this) re-seats the scene on entry and every 10 km walked
    if (++this.sweepAcc < COLLIDER_SWEEP_TICKS) return;
    this.sweepAcc = 0;
    const px = atlas.localX, pz = atlas.localZ;
    const want = new Set<string>();
    // Premium shells: colliders ride the near-ring; VISIBILITY rides a wider
    // bubble (172 composed shells would be ~3k draw calls if all rendered).
    for (const e of this.premium) {
      if (e.hidden) continue;
      const d = Math.hypot(e.lot.x - px, e.lot.z - pz);
      e.group.visible = d < 560;
      if (d > COLLIDER_FAR_M) continue;
      const id = `hivelot:${e.lot.key}`;
      if (d <= COLLIDER_NEAR_M || this.colliderIds.has(id)) {
        want.add(id);
        if (!this.colliderIds.has(id)) {
          this.physics.addStaticCollider(id, { shape: 'box', halfExtents: [e.lot.w / 2, e.lot.h / 2, e.lot.d / 2] },
            { x: e.lot.x, y: e.lot.h / 2, z: e.lot.z, yaw: e.lot.yaw, scale: 1 });
          this.colliderIds.add(id);
        }
      }
    }
    for (const id of [...this.colliderIds]) {
      if (!want.has(id)) { this.physics.removeStaticCollider(id); this.colliderIds.delete(id); }
    }
  }

  // ── Market data + the lease/manage/browse UX ─────────────────────────────

  private lotRow(lotKey: string): { lotId: bigint; lotKey: string; tier: string; renter: { isEqual(o: unknown): boolean; toHexString(): string }; storeName: string; expiresAt: { microsSinceUnixEpoch: bigint } } | null {
    try {
      for (const r of (atlas.conn?.db as unknown as { merchantLot: { iter(): Iterable<{ lotId: bigint; lotKey: string; tier: string; renter: { isEqual(o: unknown): boolean; toHexString(): string }; storeName: string; expiresAt: { microsSinceUnixEpoch: bigint } }> } }).merchantLot.iter()) {
        if (r.lotKey === lotKey) return r;
      }
    } catch { /* pre-publish */ }
    return null;
  }

  private listingsOf(lotId: bigint): Array<{ listingId: bigint; label: string; detail: string; priceSov: bigint; stock: number }> {
    try {
      return [...(atlas.conn?.db as unknown as { merchantListing: { iter(): Iterable<{ listingId: bigint; lotId: bigint; label: string; detail: string; priceSov: bigint; stock: number }> } }).merchantListing.iter()]
        .filter((l) => l.lotId === lotId);
    } catch { return []; }
  }

  private ensureMarketSubs(): void {
    if (this.subbedMarket || !atlas.conn) return;
    this.subbedMarket = true;
    try {
      atlas.conn.subscriptionBuilder().subscribe([
        'SELECT * FROM merchant_lot', 'SELECT * FROM merchant_listing', 'SELECT * FROM merchant_sale',
      ]);
    } catch { this.subbedMarket = false; }
  }

  private startUiLoop(): void {
    if (this.uiTimer !== null) return;
    if (!this.prompt) {
      const d = document.createElement('div');
      d.style.cssText = 'position:fixed;left:50%;bottom:120px;transform:translateX(-50%);z-index:36;'
        + 'background:color-mix(in srgb, var(--ax-bg) 85%, transparent);border:1px solid var(--ax-line);border-radius:var(--ax-radius);padding:5px 14px;'
        + 'font:13px var(--ax-font);color:var(--ax-text);display:none;pointer-events:none';
      document.body.appendChild(d);
      this.prompt = d;
    }
    this.onZ = (e: KeyboardEvent) => {
      if (e.code !== 'KeyZ' || e.ctrlKey || e.metaKey || e.altKey) return;
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA')) return;
      if (this.nearLotKey) this.openPanel(this.nearLotKey);
    };
    window.addEventListener('keydown', this.onZ);
    this.uiTimer = window.setInterval(() => this.uiTick(), 500);
  }

  /** Which lot the player is engaging: inside its interior, or at its door. */
  private engagedLot(): PremiumLot | null {
    if (atlas.interiorId?.startsWith('hivelot:')) {
      return lotByKey(atlas.interiorId.slice('hivelot:'.length));
    }
    let best: PremiumLot | null = null;
    let bd = 8;
    for (const e of this.premium) {
      // The door sits on local -Z, at the plaza-facing edge.
      const dx = e.lot.x - Math.sin(e.lot.yaw) * (e.lot.d / 2 + 1.2);
      const dz = e.lot.z - Math.cos(e.lot.yaw) * (e.lot.d / 2 + 1.2);
      const d = Math.hypot(dx - atlas.localX, dz - atlas.localZ);
      if (d < bd) { bd = d; best = e.lot; }
    }
    return best;
  }

  /** Every lease row by lot key — one table pass per tick instead of one linear scan per lot (172 lots × 2). */
  private lotRowMap(): Map<string, LotRowLite> {
    const out = new Map<string, LotRowLite>();
    try {
      for (const r of (atlas.conn?.db as unknown as { merchantLot: { iter(): Iterable<LotRowLite> } }).merchantLot.iter()) out.set(r.lotKey, r);
    } catch { /* pre-publish */ }
    return out;
  }

  private uiTick(): void {
    if (!this.root) return;
    const rows = this.lotRowMap();
    // Fascia signs + enterable names follow the lease rows.
    for (const e of this.premium) {
      this.drawSign(e);
      const ent = enterables.get(`hivelot:${e.lot.key}`);
      if (ent) {
        const row = rows.get(e.lot.key) ?? null;
        const active = row && Number(row.expiresAt.microsSinceUnixEpoch / 1000n) > Date.now();
        ent.name = active ? row!.storeName : `vacant ${e.lot.tier}`;
      }
    }
    // The Z prompt.
    const lot = this.engagedLot();
    this.nearLotKey = lot?.key ?? '';
    if (this.prompt) {
      if (!lot) this.prompt.style.display = 'none';
      else {
        const row = rows.get(lot.key) ?? null;
        const active = row && Number(row.expiresAt.microsSinceUnixEpoch / 1000n) > Date.now();
        const me = atlas.conn?.identity;
        const mine = active && me ? row!.renter.isEqual(me) : false;
        this.prompt.textContent = !active
          ? `Z — lease this ${lot.tier} (${LOT_RENT[lot.tier]} ⓒ/week)`
          : mine ? `Z — manage ${row!.storeName}` : `Z — browse ${row!.storeName}`;
        this.prompt.style.display = 'block';
      }
    }
    this.refreshPlates();
    if (this.panel?.isOpen() && this.panelLotKey) {
      // NEVER rebuild the DOM out from under a typing merchant — the 500 ms
      // refresh was destroying the store-name input (and its focus) twice a
      // second, making it unclickable in practice.
      const fe = document.activeElement as HTMLElement | null;
      const typing = !!fe && !!this.panel.body.contains(fe) && (fe.tagName === 'INPUT' || fe.tagName === 'TEXTAREA');
      if (!typing) this.renderPanel();
    }
  }

  // ── In-store product plates along the display walls ──────────────────────
  private refreshPlates(): void {
    const inside = atlas.interiorId?.startsWith('hivelot:') ? atlas.interiorId.slice('hivelot:'.length) : '';
    const lot = inside ? lotByKey(inside) : null;
    const row = lot ? this.lotRow(lot.key) : null;
    const listings = row ? this.listingsOf(row.lotId).slice(0, 8) : [];
    const sig = `${inside}|${listings.map((l) => `${l.listingId}:${l.label}:${l.priceSov}:${l.stock}`).join(',')}`;
    if (sig === this.platesSig) return;
    this.platesSig = sig;
    for (const m of this.plates) { this.scene.remove(m); disposeLater(m.geometry); disposeLater(m.material as THREE.Material); }
    this.plates = [];
    this.platesFor = inside;
    if (!lot || listings.length === 0) return;
    const c = Math.cos(lot.yaw), sn = Math.sin(lot.yaw);
    const toWorld = (lx: number, lz: number): { x: number; z: number } =>
      ({ x: lot.x + lx * c + lz * sn, z: lot.z - lx * sn + lz * c });
    listings.forEach((l, i) => {
      const cv = document.createElement('canvas');
      cv.width = 320; cv.height = 176;
      const g = cv.getContext('2d')!;
      g.fillStyle = '#1a1610'; g.fillRect(0, 0, 320, 176);
      g.strokeStyle = '#c8a24e'; g.lineWidth = 4; g.strokeRect(4, 4, 312, 168);
      g.textAlign = 'center';
      g.font = '600 26px Georgia, serif'; g.fillStyle = '#f0e2c2';
      g.fillText(l.label.slice(0, 20), 160, 44);
      g.font = '15px system-ui'; g.fillStyle = '#b8ac90';
      g.fillText(l.detail.slice(0, 34), 160, 78);
      g.font = '700 30px Georgia, serif'; g.fillStyle = '#e8c87a';
      g.fillText(`${l.priceSov} ⓒ`, 160, 126);
      g.font = '13px system-ui'; g.fillStyle = '#8fa88f';
      g.fillText(l.stock === -1 ? 'service — made to order' : `${l.stock} in stock`, 160, 156);
      const tex = new THREE.CanvasTexture(cv);
      tex.colorSpace = THREE.SRGBColorSpace;
      const mesh = new THREE.Mesh(new THREE.PlaneGeometry(1.7, 0.95), new THREE.MeshBasicMaterial({ map: tex, toneMapped: false }));
      const side = i % 2 === 0 ? -1 : 1;
      const lx = side * (lot.w / 2 - 1.35);
      const lz = -lot.d / 2 + 2.8 + Math.floor(i / 2) * 2.4;
      const wp = toWorld(lx, lz);
      mesh.position.set(wp.x, 1.9, wp.z);
      mesh.rotation.y = lot.yaw + (side === -1 ? -Math.PI / 2 : Math.PI / 2) + Math.PI;
      this.scene.add(mesh);
      this.plates.push(mesh);
    });
  }

  // ── The lease / manage / browse panel ────────────────────────────────────
  private esc(v: string): string {
    return v.replace(/[&<>"']/g, (ch) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch] as string));
  }

  private openPanel(lotKey: string): void {
    this.panelLotKey = lotKey;
    this.panelSig = '';
    // Release the mouse-look capture — the cursor must be free to click the
    // store-name box (the StructurePlacement/AdminEditor law).
    if (document.pointerLockElement) document.exitPointerLock();
    if (!this.panel) {
      this.panel = makeWindow({
        id: 'hive-store', title: 'Merchant Hive', icon: 'orbit',
        width: 460, height: 480, minWidth: 380, minHeight: 320, x: 180, y: 100,
      });
    }
    this.panel.setOpen(true);
    this.renderPanel();
  }

  /** Text boxes swallow their keystrokes — half the keyboard is menu
   *  shortcuts (V, Z, K, F9…), and window-level handlers would otherwise
   *  fire while the merchant types a store name. */
  private guardInputs(scope: HTMLElement): void {
    for (const el of Array.from(scope.querySelectorAll<HTMLElement>('input, textarea'))) {
      for (const evt of ['keydown', 'keyup', 'keypress'] as const) {
        el.addEventListener(evt, (e) => e.stopPropagation());
      }
    }
  }

  private renderPanel(): void {
    const body = this.panel?.body;
    const conn = atlas.conn;
    const lot = lotByKey(this.panelLotKey);
    if (!body || !conn?.identity || !lot) return;
    const me = conn.identity;
    const row = this.lotRow(lot.key);
    const active = row && Number(row.expiresAt.microsSinceUnixEpoch / 1000n) > Date.now();
    const mine = active && row!.renter.isEqual(me);
    const R = conn.reducers as unknown as {
      rentMerchantLot(a: { lotKey: string; storeName: string }): Promise<void>;
      renewMerchantLot(a: { lotKey: string }): Promise<void>;
      releaseMerchantLot(a: { lotKey: string }): Promise<void>;
      setMerchantListing(a: { lotKey: string; listingId: bigint; label: string; detail: string; priceSov: bigint; stock: number }): Promise<void>;
      removeMerchantListing(a: { listingId: bigint }): Promise<void>;
      buyMerchantListing(a: { listingId: bigint; qty: number }): Promise<void>;
    };
    // Rebuild only when the DATA changed — a static rebuild would still smash
    // inputs the merchant is about to click.
    const listingsSig = active ? this.listingsOf(row!.lotId).map((l) => `${l.listingId}:${l.label}:${l.priceSov}:${l.stock}`).join(',') : '';
    const salesSig = (() => {
      try {
        return active && mine
          ? [...(conn.db as unknown as { merchantSale: { iter(): Iterable<{ saleId: bigint; lotId: bigint }> } }).merchantSale.iter()].filter((sl) => sl.lotId === row!.lotId).length
          : 0;
      } catch { return 0; }
    })();
    const sig = `${this.panelLotKey}|${active ? row!.storeName : 'vacant'}|${active ? row!.expiresAt.microsSinceUnixEpoch : 0n}|${mine}|${listingsSig}|${salesSig}`;
    if (sig === this.panelSig) return;
    this.panelSig = sig;
    const html: string[] = [];
    const inputCss = 'background:var(--ax-bg2);border:1px solid var(--ax-line);border-radius:var(--ax-radius);color:var(--ax-text);padding:3px 8px;font:12px var(--ax-font)';
    if (!active) {
      html.push(`<div style="font:700 15px Georgia,var(--ax-font);color:var(--ax-hi)">FOR LEASE — ${lot.tier} · ${LOT_RENT[lot.tier]} ⓒ/week</div>`);
      html.push('<div style="opacity:.7;margin:4px 0 8px">A week of tenure, a name on the fascia, twelve shelves for your wares. Real merchants welcome.</div>');
      html.push(`<div style="display:flex;gap:6px"><input data-sname placeholder="store name" maxlength="32" style="flex:1;${inputCss}">`
        + '<button data-lease style="padding:4px 14px">Lease</button></div>');
    } else {
      const days = Math.max(0, Math.round((Number(row!.expiresAt.microsSinceUnixEpoch / 1000n) - Date.now()) / 86_400_000));
      html.push(`<div style="font:700 16px Georgia,var(--ax-font);color:var(--ax-hi)">${this.esc(row!.storeName)}</div>`);
      html.push(`<div style="opacity:.7">${lot.tier} · lease ${days} day${days === 1 ? '' : 's'} left${mine ? ' · yours' : ''}</div>`);
      const listings = this.listingsOf(row!.lotId);
      if (listings.length === 0) html.push('<div style="opacity:.55;margin-top:8px">The shelves are bare.</div>');
      for (const l of listings) {
        html.push(`<div style="margin-top:6px;padding:6px 8px;border:1px solid var(--ax-line);border-radius:var(--ax-radius)">`
          + `<div style="display:flex;gap:8px;align-items:baseline"><b style="flex:1">${this.esc(l.label)}</b>`
          + `<span style="color:var(--ax-hi);font:700 14px Georgia,var(--ax-font)">${l.priceSov} ⓒ</span></div>`
          + (l.detail ? `<div style="opacity:.7;font-size:12px">${this.esc(l.detail)}</div>` : '')
          + `<div style="display:flex;gap:6px;align-items:center;margin-top:4px"><span style="opacity:.6;font-size:11px;flex:1">${l.stock === -1 ? 'service — made to order' : `${l.stock} in stock`}</span>`
          + (mine
            ? `<button data-rm="${l.listingId}" style="padding:2px 10px;font-size:11px;border-color:color-mix(in srgb, var(--ax-bad) 34%, var(--ax-line));color:var(--ax-bad)">remove</button>`
            : `<button data-buy="${l.listingId}" style="padding:2px 12px;font-size:11px"${l.stock === 0 ? ' disabled' : ''}>Buy</button>`)
          + '</div></div>');
      }
      if (mine) {
        html.push('<div style="font:700 11px var(--ax-font);letter-spacing:.08em;color:var(--ax-hi);margin:12px 0 4px;text-transform:uppercase">add a listing</div>');
        html.push(`<div style="display:flex;flex-direction:column;gap:5px">`
          + `<input data-llabel placeholder="what you sell (label)" maxlength="48" style="${inputCss}">`
          + `<input data-ldetail placeholder="detail (shown to browsers)" maxlength="160" style="${inputCss}">`
          + `<div style="display:flex;gap:6px"><input data-lprice type="number" placeholder="price ⓒ" value="25" style="width:90px;${inputCss}">`
          + `<input data-lstock type="number" placeholder="stock (−1 = service)" value="10" style="width:150px;${inputCss}">`
          + '<button data-ladd style="flex:1;padding:4px 10px">Put on the shelf</button></div></div>');
        html.push(`<div style="display:flex;gap:8px;margin-top:12px">`
          + `<button data-renew style="padding:4px 12px">Renew (+7 d · ${LOT_RENT[lot.tier as LotTier]} ⓒ)</button>`
          + '<button data-release style="padding:4px 12px;border-color:color-mix(in srgb, var(--ax-bad) 34%, var(--ax-line));color:var(--ax-bad)">Release the lease</button></div>');
        // Recent sales (the fulfillment trail).
        try {
          const sales = [...(conn.db as unknown as { merchantSale: { iter(): Iterable<{ saleId: bigint; lotId: bigint; label: string; priceSov: bigint; qty: number }> } }).merchantSale.iter()]
            .filter((sl) => sl.lotId === row!.lotId).slice(-5).reverse();
          if (sales.length) {
            html.push('<div style="font:700 11px var(--ax-font);letter-spacing:.08em;color:var(--ax-hi);margin:12px 0 4px;text-transform:uppercase">recent sales</div>');
            for (const sl of sales) html.push(`<div style="opacity:.75;font-size:12px">• ${this.esc(sl.label)} ×${sl.qty} — ${sl.priceSov * BigInt(sl.qty)} ⓒ</div>`);
          }
        } catch { /* fine */ }
      }
    }
    body.innerHTML = `<div style="padding:10px 12px;color:var(--ax-text);font:13px/1.5 var(--ax-font)">${html.join('')}</div>`;
    this.guardInputs(body);
    const toast = (msg: string, err = false): void => { bus.emit('atlas:status', { text: err ? `⚠ ${msg}` : msg }); };
    // Enter in the name box leases directly.
    body.querySelector<HTMLInputElement>('[data-sname]')?.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') (body.querySelector<HTMLButtonElement>('[data-lease]'))?.click();
    });
    body.querySelector('[data-lease]')?.addEventListener('click', () => {
      const name = (body.querySelector<HTMLInputElement>('[data-sname]')?.value ?? '').trim();
      void R.rentMerchantLot({ lotKey: lot.key, storeName: name })
        .then(() => toast(`🏪 ${name} opens its doors — welcome to the Hive`))
        .catch((e: Error) => toast(String(e.message ?? e), true));
    });
    body.querySelector('[data-renew]')?.addEventListener('click', () => {
      void R.renewMerchantLot({ lotKey: lot.key }).then(() => toast('Lease renewed — another week on the square')).catch((e: Error) => toast(String(e.message ?? e), true));
    });
    body.querySelector('[data-release]')?.addEventListener('click', () => {
      void R.releaseMerchantLot({ lotKey: lot.key }).then(() => toast('The keys are back with the Hive')).catch((e: Error) => toast(String(e.message ?? e), true));
    });
    body.querySelector('[data-ladd]')?.addEventListener('click', () => {
      const label = (body.querySelector<HTMLInputElement>('[data-llabel]')?.value ?? '').trim();
      const detail = (body.querySelector<HTMLInputElement>('[data-ldetail]')?.value ?? '').trim();
      const price = BigInt(Math.max(1, Number(body.querySelector<HTMLInputElement>('[data-lprice]')?.value ?? 1) | 0));
      const stock = Math.max(-1, Number(body.querySelector<HTMLInputElement>('[data-lstock]')?.value ?? 0) | 0);
      void R.setMerchantListing({ lotKey: lot.key, listingId: 0n, label, detail, priceSov: price, stock })
        .then(() => toast('On the shelf')).catch((e: Error) => toast(String(e.message ?? e), true));
    });
    for (const el of Array.from(body.querySelectorAll<HTMLButtonElement>('[data-rm]'))) {
      el.addEventListener('click', () => void R.removeMerchantListing({ listingId: BigInt(el.dataset.rm!) })
        .then(() => toast('Taken off the shelf')).catch((e: Error) => toast(String(e.message ?? e), true)));
    }
    for (const el of Array.from(body.querySelectorAll<HTMLButtonElement>('[data-buy]'))) {
      el.addEventListener('click', () => void R.buyMerchantListing({ listingId: BigInt(el.dataset.buy!), qty: 1 })
        .then(() => toast('💰 Purchased — the merchant has your order')).catch((e: Error) => toast(String(e.message ?? e), true)));
    }
  }

  private release(): void {
    if (!this.root) return;
    this.scene.remove(this.root);
    // Deferred (warp-teardown law: this runs from world-entered mid-frame),
    // once per shared geometry, and the InstancedMesh instance buffers too
    // (instanceMatrix/instanceColor were never released before).
    const seen = new Set<object>();
    this.root.traverse((o) => {
      const mesh = o as THREE.Mesh & { isInstancedMesh?: boolean; dispose?(): void };
      if (mesh.geometry && !seen.has(mesh.geometry)) { seen.add(mesh.geometry); disposeLater(mesh.geometry); }
      const mats = Array.isArray(mesh.material) ? mesh.material : mesh.material ? [mesh.material] : [];
      for (const mat of mats) if (!seen.has(mat)) { seen.add(mat); disposeLater(mat); } // textures stay in the AssetModule cache (shared)
      if (mesh.isInstancedMesh && typeof mesh.dispose === 'function') disposeLater(mesh as unknown as { dispose(): void });
    });
    this.root = null;
    this.glow = null;
    this.builtSeed = '';
    for (const id of this.colliderIds) this.physics.removeStaticCollider(id);
    this.colliderIds.clear();
    // The premium district: enterables, square colliders, plates, UI.
    for (const e of this.premium) {
      enterables.delete(`hivelot:${e.lot.key}`);
      disposeLater(e.signTex);
    }
    this.premium = [];
    for (const id of this.squareColliders) this.physics.removeStaticCollider(id);
    this.squareColliders = [];
    for (const m of this.plates) { this.scene.remove(m); disposeLater(m.geometry); disposeLater(m.material as THREE.Material); }
    this.plates = [];
    this.platesSig = '';
    if (this.uiTimer !== null) { window.clearInterval(this.uiTimer); this.uiTimer = null; }
    if (this.onZ) { window.removeEventListener('keydown', this.onZ); this.onZ = null; }
    if (this.prompt) { this.prompt.remove(); this.prompt = null; }
    if (this.panel?.isOpen()) this.panel.setOpen(false);
    this.nearLotKey = '';
  }

  dispose(): void {
    this.release();
  }
}
